import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Globe,
  Sparkles,
  Send,
  Trash2,
  ExternalLink,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Save,
  Coins,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useContent,
  useDeleteContent,
  useDispatchContent,
  useGenerateContent,
  useModels,
  useUpdateContent,
} from '@/hooks/useContent';
import { usePreferences } from '@/hooks/usePreferences';
import { ProviderModelSelect } from '@/components/ProviderModelSelect';
import { useSites, useSiteCategories, useSiteTags } from '@/hooks/useSites';
import { TaxonomyPicker } from '@/components/content/TaxonomyPicker';
import { useFeatures } from '@/hooks/useFeatures';
import { FeaturedImagePanel } from '@/components/content/FeaturedImagePanel';
import { BodyImagesPanel } from '@/components/content/BodyImagesPanel';
import { useConfirm } from '@/components/ConfirmDialog';
import { RichEditor } from '@/components/content/RichEditor';
import { ApiError } from '@/lib/api-client';
import { formatDate, formatUsd } from '@/lib/utils';
import { cn } from '@/lib/utils';

export function ContentDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data, isPending } = useContent(id);
  const update = useUpdateContent(id);
  const del = useDeleteContent();
  const generate = useGenerateContent(id);
  const dispatch = useDispatchContent(id);
  const sites = useSites();
  const features = useFeatures();
  const models = useModels();
  const prefs = usePreferences();
  const [webGrounded, setWebGrounded] = useState(false);
  // User can swap the AI model at regen time. Initialised from
  // content.ai_model (if already set) → prefs default → catalog default.
  const [modelOverride, setModelOverride] = useState<string>('');

  // Two-level Sağlayıcı → Model grouping. Mirrors the ContentNew picker
  // so users see the same layout wherever they pick a model.
  const FAMILY_LABEL: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic Claude',
    gemini: 'Google Gemini',
    xai: 'xAI Grok',
    deepseek: 'DeepSeek',
    meta: 'Meta Llama',
    mistral: 'Mistral',
    qwen: 'Qwen (Alibaba)',
    minimax: 'MiniMax',
    glm: 'Zhipu GLM',
    kimi: 'Moonshot Kimi',
    ernie: 'Baidu ERNIE',
    perplexity: 'Perplexity Sonar',
    cohere: 'Cohere',
    nvidia: 'NVIDIA',
    other: 'Diğer',
  };
  const FAMILY_ORDER = [
    'openai', 'anthropic', 'gemini', 'xai', 'deepseek', 'meta', 'mistral',
    'qwen', 'minimax', 'glm', 'kimi', 'ernie', 'perplexity', 'cohere', 'nvidia', 'other',
  ];
  const chatModelGroups = useMemo(() => {
    if (!models.data) return [];
    const byFamily: Record<string, typeof models.data.models> = {};
    for (const m of models.data.models) (byFamily[m.family] = byFamily[m.family] ?? []).push(m);
    return FAMILY_ORDER.filter((f) => byFamily[f]?.length).map((id) => ({
      id,
      label: FAMILY_LABEL[id] ?? id,
      items: (byFamily[id] ?? []).map((m) => ({
        id: m.id,
        label: m.label,
        suffix: `$${(m.credits_per_word / 1_000_000).toFixed(6)}/kelime${m.note ? ` · ${m.note}` : ''}`,
        deprecated: false,
      })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models.data]);


  const [title, setTitle] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [siteId, setSiteId] = useState<string | null>(null);
  const [dispatchStatus, setDispatchStatus] = useState<'publish' | 'draft' | 'pending'>('publish');
  const [publishMessage, setPublishMessage] = useState<string | null>(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const categoriesQ = useSiteCategories(siteId);
  const tagsQ = useSiteTags(siteId);

  // Reset taxonomy selection when the target site changes — category
  // ids are WP-specific and can't be shared across sites.
  useEffect(() => {
    setSelectedCategoryIds([]);
    setSelectedTagIds([]);
  }, [siteId]);

  useEffect(() => {
    if (data?.content) {
      setTitle(data.content.title);
      setBodyHtml(data.content.body_html ?? '');
      setSeoTitle(data.content.seo_title ?? '');
      setSeoDescription(data.content.seo_description ?? '');
      setExcerpt(data.content.excerpt ?? '');
      setSiteId(data.content.site_id);
    }
  }, [data]);

  // Seed the inline model override: prefer what the content already used,
  // fall back to user preference, else catalog default. Only happens once
  // per content load so switching the dropdown doesn't get stomped.
  useEffect(() => {
    if (modelOverride) return;
    const fromContent = data?.content?.ai_model;
    const fromPrefs = prefs.data?.preferences.default_ai_model;
    const pick = fromContent || fromPrefs;
    if (pick) setModelOverride(pick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.content?.id, prefs.data]);

  if (isPending) return <div className="text-muted-foreground">Yükleniyor…</div>;
  if (!data?.content) return <div className="text-destructive">İçerik bulunamadı</div>;

  const c = data.content;
  const canGenerate = c.status === 'draft' || c.status === 'ready' || c.status === 'failed';
  const canDispatch = c.body_html && (c.status === 'ready' || c.status === 'failed');

  const onSave = async () => {
    await update.mutateAsync({
      title,
      body_html: bodyHtml,
      seo_title: seoTitle,
      seo_description: seoDescription,
      excerpt,
      site_id: siteId,
    });
  };

  const onGenerate = async () => {
    try {
      await generate.mutateAsync({
        ai_model: modelOverride || c.ai_model || prefs.data?.preferences.default_ai_model || 'openai/gpt-5-nano',
        include_faq: false,
        include_meta: true,
        web_grounded: webGrounded && features.web_grounding,
      });
    } catch {
      /* error shown in status badge */
    }
  };

  const onDispatch = async () => {
    setPublishMessage(null);
    try {
      const res = await dispatch.mutateAsync({
        site_id: siteId ?? undefined,
        status: dispatchStatus,
        category_ids: selectedCategoryIds.length > 0 ? selectedCategoryIds : undefined,
        tag_ids: selectedTagIds.length > 0 ? selectedTagIds : undefined,
      });
      setPublishMessage(`Yayınlandı: ${res.remote_post_url}`);
    } catch (err) {
      setPublishMessage(err instanceof ApiError ? err.message : 'Dispatch hatası');
    }
  };

  const onDelete = async () => {
    const ok = await confirm({
      title: 'İçeriği sil?',
      description: 'Bu içerik kalıcı olarak silinecek. Geri alınamaz.',
      confirmText: 'Sil',
      danger: true,
    });
    if (!ok) return;
    await del.mutateAsync(id);
    navigate({ to: '/content' });
  };

  return (
    <div className="max-w-5xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/content">
            <ArrowLeft className="w-4 h-4" /> İçeriklere dön
          </Link>
        </Button>
        <div className="flex items-start justify-between flex-wrap gap-2 mt-2">
          <div>
            <h1 className="text-2xl font-semibold">{c.title}</h1>
            <p className="text-xs text-muted-foreground mt-1">
              Durum: <StatusBadge status={c.status} />
              {' · '}Oluşturuldu: {formatDate(c.created_at)}
              {c.ai_credits_consumed != null && (
                <> · Harcanan: <span className="font-mono">{formatUsd(c.ai_credits_consumed)}</span></>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onDelete} disabled={del.isPending}>
              <Trash2 className="w-4 h-4" /> Sil
            </Button>
          </div>
        </div>
      </div>

      {c.status === 'failed' && c.error_message && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 text-destructive text-sm p-3 flex items-start gap-2">
          <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Hata</p>
            <p className="text-xs">{c.error_message}</p>
          </div>
        </div>
      )}

      {/* Generate card */}
      {canGenerate && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> {c.body_html ? 'Tekrar Üret' : 'AI ile Üret'}
            </CardTitle>
            <CardDescription>
              {c.body_html
                ? 'Mevcut içerik silinir ve yeni bir versiyon üretilir.'
                : `Focus: ${c.focus_keyword ?? '-'}`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">AI Modeli</Label>
              <ProviderModelSelect
                groups={chatModelGroups}
                value={modelOverride || c.ai_model || undefined}
                onChange={setModelOverride}
                providerLabel="Sağlayıcı"
                modelLabel="Model"
              />
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={onGenerate} disabled={generate.isPending}>
                {generate.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : c.body_html ? (
                  <RefreshCw className="w-4 h-4" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                {generate.isPending ? 'Üretiliyor…' : c.body_html ? 'Yeniden Üret' : 'Üret'}
              </Button>
              {generate.data?.credits_consumed != null && (
                <span className="text-sm text-muted-foreground inline-flex items-center gap-1 font-mono">
                  <Coins className="w-3.5 h-3.5" />
                  {formatUsd(generate.data.credits_consumed)} harcandı
                </span>
              )}
            </div>
            <div className="flex items-start gap-2 text-sm">
              <input
                id="web-grounded"
                type="checkbox"
                checked={webGrounded}
                onChange={(e) => setWebGrounded(e.target.checked)}
                disabled={!features.web_grounding}
                className="h-4 w-4 rounded border-input mt-0.5"
              />
              <div>
                <Label
                  htmlFor="web-grounded"
                  className={cn(
                    'cursor-pointer',
                    !features.web_grounding && 'text-muted-foreground cursor-not-allowed',
                  )}
                >
                  Güncel bilgi kullan <span className="text-xs text-muted-foreground">(web grounding · 4× bakiye)</span>
                </Label>
                {!features.web_grounding ? (
                  <p className="text-xs text-destructive">
                    Güncel Bilgi eklentin yok.{' '}
                    <Link to="/packages" className="underline">Eklentiyi al</Link>.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    AI, yazıyı oluştururken canlı web araması yapar — haber, istatistik, son gelişmeler için.
                  </p>
                )}
              </div>
            </div>

            <p className="text-xs text-muted-foreground pt-1 border-t">
              Görselleri aşağıdaki "Öne Çıkan Görsel" ve "İçerik Görselleri" panellerinden yönetebilirsin —
              metni yeniden üretmeden sadece görsel üretmek için oraya bak.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Featured image */}
      {c.body_html && (
        <FeaturedImagePanel
          contentId={c.id}
          r2Key={c.featured_image_r2_key}
          altText={c.featured_image_alt}
          focusKeyword={c.focus_keyword}
          templateHasImagePrompt={!!c.template_type}
        />
      )}

      {/* Body images — shows only when there are any */}
      {c.body_html && <BodyImagesPanel contentId={c.id} />}

      {/* Editor — collapsed by default via <details> to keep the page
           scroll short. open={false} would be sticky but we want default
           collapsed behavior; leaving no `open` attribute does that. */}
      {c.body_html && (
        <Card>
          <details>
            <summary className="list-none cursor-pointer [&::-webkit-details-marker]:hidden">
              <CardHeader className="hover:bg-accent/40 rounded-t-lg flex-row items-center justify-between gap-2 space-y-0">
                <div>
                  <CardTitle className="text-base">İçerik</CardTitle>
                  <CardDescription>
                    HTML desteklenir. Kaydetmeden ayrılırsan değişiklikler kaybolur.
                  </CardDescription>
                </div>
                <span className="text-xs text-muted-foreground group-open:hidden">
                  Aç ↓
                </span>
              </CardHeader>
            </summary>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Başlık</Label>
                <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="excerpt">Özet</Label>
                <Input id="excerpt" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} maxLength={500} />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="seo_title">SEO Başlık</Label>
                  <Input
                    id="seo_title"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    maxLength={200}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seo_desc">SEO Açıklama</Label>
                  <Input
                    id="seo_desc"
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    maxLength={500}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Gövde</Label>
                <RichEditor value={bodyHtml} onChange={setBodyHtml} contentId={c.id} />
              </div>
              <Button onClick={onSave} disabled={update.isPending}>
                <Save className="w-4 h-4" /> {update.isPending ? 'Kaydediliyor…' : 'Kaydet'}
              </Button>
            </CardContent>
          </details>
        </Card>
      )}

      {/* Live preview — collapsed by default */}
      {bodyHtml && (
        <Card>
          <details>
            <summary className="list-none cursor-pointer [&::-webkit-details-marker]:hidden">
              <CardHeader className="hover:bg-accent/40 rounded-t-lg flex-row items-center justify-between gap-2 space-y-0">
                <CardTitle className="text-base">Önizleme</CardTitle>
                <span className="text-xs text-muted-foreground">Aç ↓</span>
              </CardHeader>
            </summary>
            <CardContent>
              <article
                className="prose prose-sm max-w-none dark:prose-invert [&_h2]:mt-6 [&_h2]:mb-2 [&_h3]:mt-4 [&_h3]:mb-1 [&_p]:my-2"
                dangerouslySetInnerHTML={{ __html: bodyHtml }}
              />
            </CardContent>
          </details>
        </Card>
      )}

      {/* Dispatch */}
      {canDispatch && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Send className="w-4 h-4" /> Siteye Gönder
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="site">Hedef Site</Label>
                <select
                  id="site"
                  value={siteId ?? ''}
                  onChange={(e) => setSiteId(e.target.value || null)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">Seç…</option>
                  {sites.data?.sites.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ds">Yayın Durumu</Label>
                <select
                  id="ds"
                  value={dispatchStatus}
                  onChange={(e) => setDispatchStatus(e.target.value as typeof dispatchStatus)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="publish">Yayınla</option>
                  <option value="draft">Taslak</option>
                  <option value="pending">Onay bekliyor</option>
                </select>
              </div>
            </div>
            {siteId && (
              <div className="grid gap-4 sm:grid-cols-2">
                <TaxonomyPicker
                  label="Kategoriler"
                  emptyHint="Kategori seçmezsen WP varsayılan kategoriyi kullanır (Uncategorized)."
                  items={categoriesQ.data?.categories.map((c) => ({ id: c.id, name: c.name, count: c.count })) ?? []}
                  loading={categoriesQ.isPending}
                  error={categoriesQ.error instanceof Error ? categoriesQ.error.message : null}
                  selected={selectedCategoryIds}
                  onToggle={(id) =>
                    setSelectedCategoryIds((prev) =>
                      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                    )
                  }
                />
                <TaxonomyPicker
                  label="Etiketler"
                  emptyHint="Etiketler opsiyonel."
                  items={tagsQ.data?.tags.map((t) => ({ id: t.id, name: t.name, count: t.count })) ?? []}
                  loading={tagsQ.isPending}
                  error={tagsQ.error instanceof Error ? tagsQ.error.message : null}
                  selected={selectedTagIds}
                  onToggle={(id) =>
                    setSelectedTagIds((prev) =>
                      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                    )
                  }
                />
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Button onClick={onDispatch} disabled={!siteId || dispatch.isPending}>
                {dispatch.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Gönder
              </Button>
              {publishMessage && (
                <span
                  className={cn(
                    'text-sm',
                    publishMessage.startsWith('Yayınlandı')
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-destructive',
                  )}
                >
                  {publishMessage}
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Published info */}
      {c.remote_post_url && (
        <Card>
          <CardContent className="p-4 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <span>Yayında · {formatDate(c.published_at)}</span>
            </div>
            <a
              href={c.remote_post_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-primary hover:underline inline-flex items-center gap-1"
            >
              {c.remote_post_url.replace(/^https?:\/\//, '')} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    draft: { label: 'Taslak', cls: 'bg-muted text-muted-foreground' },
    generating: { label: 'Üretiliyor…', cls: 'bg-primary/10 text-primary' },
    ready: { label: 'Hazır', cls: 'bg-primary/10 text-primary' },
    dispatching: { label: 'Gönderiliyor…', cls: 'bg-primary/10 text-primary' },
    published: { label: 'Yayında', cls: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
    failed: { label: 'Hata', cls: 'bg-destructive/10 text-destructive' },
  };
  const m = map[status] ?? { label: status, cls: 'bg-muted text-muted-foreground' };
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium', m.cls)}>
      {m.label}
    </span>
  );
}

// Silence unused import (Globe) when detail page doesn't render sites icon in certain layouts
void Globe;
